import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LeadActivity } from '@spike/client-model';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { FunctionComponent } from 'react';
import { getLeadActivityLabel } from './leadActivityLabels';
import { EMPTY_METRIC, formatLeadDateTime } from '../leadMetrics';
import styles from './LeadActivityRow.module.scss';

const DETAIL_SEPARATOR = ' · ';
const STAGE_ARROW = '→';

const buildDetailLines = (activity: LeadActivity): Array<string> => {
    if (!activity.details) {
        return [];
    }

    const hasStageChange = Boolean(activity.detailsData?.stageFrom && activity.detailsData?.stageTo);

    if (!hasStageChange) {
        return [activity.details];
    }

    const segments = activity.details.split(DETAIL_SEPARATOR);
    const stageIndex = segments.findIndex(segment => segment.includes(STAGE_ARROW));

    if (stageIndex === -1) {
        return [activity.details];
    }

    const rest = segments.filter((_, index) => index !== stageIndex).join(DETAIL_SEPARATOR);

    return rest ? [rest, segments[stageIndex]] : [segments[stageIndex]];
};

interface LeadActivityRowProps {
    activity: LeadActivity;
    timeZone: string;
}

const LeadActivityRow: FunctionComponent<LeadActivityRowProps> = ({ activity, timeZone }) => {
    const { date, time } = formatLeadDateTime(activity.timestamp, timeZone);
    const detailLines = buildDetailLines(activity);

    return (
        <TableRow>
            <TableCell>
                <span className={styles.dateTime}>
                    <span className={styles.date}>{date}</span>
                    <span className={styles.time}>{time}</span>
                </span>
            </TableCell>
            <TableCell>{getLeadActivityLabel(activity.event)}</TableCell>
            <TableCell className={styles.detailsCell}>
                <span className={styles.details}>
                    {detailLines.length === 0 ? (
                        <span>{EMPTY_METRIC}</span>
                    ) : (
                        detailLines.map(line => <span key={line}>{line}</span>)
                    )}
                    <span className={styles.location}>
                        {activity.location ? (
                            <>
                                <FontAwesomeIcon
                                    icon={faLocationDot}
                                    className={styles.locationIcon}
                                />
                                {activity.location.name}
                            </>
                        ) : (
                            EMPTY_METRIC
                        )}
                    </span>
                </span>
            </TableCell>
            <TableCell>{activity.performedBy?.name ?? 'System'}</TableCell>
        </TableRow>
    );
};

export default LeadActivityRow;
